import React, { useState, useEffect } from 'react';
import { teamsAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaUsers, FaFutbol, FaMapMarkerAlt } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import PageCover from '../components/PageCover';
import { useLanguage } from '../context/LanguageContext';

const GroupsPage = () => {
  const { language } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await teamsAPI.getAll();
      const raw = response?.data;
      const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.teams) ? raw.teams : []);
      setTeams(list);
    } catch (error) {
      console.error('Erreur chargement équipes:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Organiser les équipes par groupe (group_number peut être null, undefined, string ou number)
  const teamsByGroup = teams.reduce((acc, team) => {
    const g = team.group_number;
    const group = g !== undefined && g !== null && g !== '' ? Number(g) : 1;
    if (!acc[group]) acc[group] = [];
    acc[group].push(team);
    return acc;
  }, {});

  const text = {
    ar: {
      title: 'تركيبة المجموعات',
      subtitle: 'تقسيم الفرق المشاركة في بطولة المستوى الثاني',
      group: 'المجموعة',
      teams: 'فريق',
      city: 'المدينة'
    },
    fr: {
      title: 'Composition des Groupes',
      subtitle: 'Répartition des équipes participantes au championnat Niveau 2',
      group: 'Groupe',
      teams: 'Équipes',
      city: 'Ville'
    }
  };

  const t = text[language];

  return (
    <div style={styles.container}>
      <PageCover title={t.title} subtitle={t.subtitle} icon={FaUsers} />

      {/* Stats */}
      <section style={styles.statsSection}>
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statNumber}>6</span>
            <span style={styles.statLabel}>{language === 'ar' ? 'مجموعات' : 'Groupes'}</span>
          </div>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{teams.length}</span>
            <span style={styles.statLabel}>{t.teams}</span>
          </div>
        </div>
      </section>

      {/* Groups */}
      <section style={styles.groupsSection}>
        {[1, 2, 3, 4, 5, 6].map(groupNum => {
          const groupTeams = teamsByGroup[groupNum] || [];
          
          return (
            <div key={groupNum} style={styles.groupContainer}>
              <div style={styles.groupHeader}>
                <FaUsers style={styles.groupIcon} />
                <h2 style={styles.groupTitle}>
                  {t.group} {groupNum}
                </h2>
                <span style={styles.groupCount}>
                  ({groupTeams.length} {t.teams})
                </span>
              </div>

              <div style={styles.teamsGrid}>
                {groupTeams.map((team, index) => (
                  <div 
                    key={team.id}
                    style={{
                      ...styles.teamCard,
                      animationDelay: `${index * 0.05}s`
                    }}
                  >
                    <div style={styles.teamLogoContainer}>
                      {getLogoUrl(team.logo_url) ? (
                        <img 
                          src={getLogoUrl(team.logo_url)} 
                          alt={team.name}
                          style={styles.teamLogo}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{...styles.logoPlaceholder, display: getLogoUrl(team.logo_url) ? 'none' : 'flex'}}>
                        <FaFutbol size={40} color="#c41e3a" />
                      </div>
                    </div>

                    <div style={styles.teamInfo}>
                      <h3 style={styles.teamName}>{team.name}</h3>
                      <div style={styles.teamMeta}>
                        <FaMapMarkerAlt style={styles.metaIcon} />
                        <span style={styles.metaText}>{team.city}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  statsSection: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px 16px'
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginTop: '30px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'white',
    padding: '20px 40px',
    borderRadius: '15px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    border: '1px solid #e8ecf0'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#1a365d'
  },
  statLabel: {
    fontSize: '16px',
    marginTop: '5px',
    color: '#555'
  },
  groupsSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '60px 20px'
  },
  groupContainer: {
    marginBottom: '60px',
    background: 'white',
    borderRadius: '20px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '30px',
    paddingBottom: '20px',
    borderBottom: '3px solid #1a365d'
  },
  groupIcon: {
    fontSize: '32px',
    color: '#1a365d'
  },
  groupTitle: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0
  },
  groupCount: {
    fontSize: '18px',
    color: '#666',
    marginLeft: 'auto'
  },
  teamsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '25px'
  },
  teamCard: {
    background: '#f9f9f9',
    borderRadius: '15px',
    padding: '25px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '15px',
    transition: 'all 0.3s',
    cursor: 'pointer',
    border: '2px solid transparent',
    animation: 'fadeInUp 0.6s ease forwards',
    opacity: 0
  },
  teamLogoContainer: {
    width: '100px',
    height: '100px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    borderRadius: '50%',
    padding: '15px',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
  },
  teamLogo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain'
  },
  logoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  teamInfo: {
    textAlign: 'center',
    width: '100%'
  },
  teamName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px',
    minHeight: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  teamMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    color: '#666',
    fontSize: '14px'
  },
  metaIcon: {
    color: '#c41e3a',
    fontSize: '14px'
  },
  metaText: {
    fontSize: '14px'
  }
};

// Ajouter l'animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .teamCard:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    border-color: #c41e3a;
  }

  @media (max-width: 768px) {
    .teamsGrid {
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)) !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default GroupsPage;
